import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Fallback editorial cases — used when admin hasn't populated any case yet.
const FALLBACK_CASES = [
  {
    case_id: 'fallback-1',
    company: 'Banco Cooperativo',
    description:
      'Programa E-Liderança aplicado em 240 líderes em 18 meses. Engajamento subiu 38% e turnover caiu 22%.',
    image: null,
    franchise_name: 'IE Porto Alegre',
    state: 'Rio Grande do Sul',
    tags: ['Liderança', 'RH'],
  },
  {
    case_id: 'fallback-2',
    company: 'Grupo Industrial',
    description:
      'E-Vendas em duas unidades fabris: conversão B2B aumentou 31% no primeiro trimestre após o treinamento.',
    image: null,
    franchise_name: 'IE Curitiba',
    state: 'Paraná',
    tags: ['Vendas', 'B2B'],
  },
  {
    case_id: 'fallback-3',
    company: 'Hospital Sul-Brasileiro',
    description:
      'Diagnóstico comportamental e intervenção na comunicação entre equipes médicas e administrativas. Reestruturação dos padrões de resposta sob pressão. NPS interno +24 pontos.',
    image: null,
    franchise_name: 'IE Florianópolis',
    state: 'Santa Catarina',
    tags: ['Cultura', 'Saúde'],
  },
  {
    case_id: 'fallback-4',
    company: 'Rede de Educação',
    description:
      'Diagnóstico Eneagrama com 60 coordenadores pedagógicos. Plano de desenvolvimento individualizado por tipo.',
    image: null,
    franchise_name: 'IE Passo Fundo',
    state: 'Rio Grande do Sul',
    tags: ['Educação', 'Coaching'],
  },
];

const Cases = () => {
  const { t } = useTranslation();
  const [cases, setCases] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/cases`);
        setCases(data?.length ? data : FALLBACK_CASES);
      } catch {
        setCases(FALLBACK_CASES);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Reveal-on-scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 },
    );
    document.querySelectorAll('#cases .animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [cases]);

  // Auto-advance every 5s, paused on hover
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || cases.length < 2) return undefined;
    const id = setInterval(() => setActiveIdx((p) => (p + 1) % cases.length), 5000);
    return () => clearInterval(id);
  }, [paused, cases.length]);

  const active = cases[activeIdx];
  const stats = useMemo(
    () => [
      { value: '+3.000', label: 'Empresas Atendidas' },
      { value: '+70.000', label: 'Eneagramados' },
      { value: cases.length || '12', label: 'Cases Documentados' },
      { value: '41', label: 'Unidades no Brasil' },
    ],
    [cases.length],
  );

  if (loading) {
    return (
      <section id="cases" className="py-24 bg-neutral-50 border-t border-dashed border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center text-neutral-400">
          <div className="animate-spin h-8 w-8 border-2 border-black/20 border-t-black rounded-full mx-auto"></div>
        </div>
      </section>
    );
  }

  return (
    <section id="cases" className="relative py-24 border-t border-neutral-900 overflow-hidden" style={{ background: 'rgba(10, 10, 10, 0.72)' }}>
      {/* Decorative orange accent line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-16 bg-gradient-to-b from-accent-orange to-transparent opacity-60"></div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block animate-on-scroll">
            {t('cases.label')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 animate-on-scroll delay-100 text-[#EAEAEA]">
            {t('cases.title_part1')} <span className="text-accent-orange">{t('cases.title_highlight')}</span>
          </h2>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto animate-on-scroll delay-200">
            {t('cases.subtitle')}
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16 animate-on-scroll">
          {stats.map((s, i) => (
            <div
              key={i}
              className="text-center p-6 bg-[#1A1A1A] rounded-xl border border-[#333333] accent-glow-card"
              data-testid={`cases-stat-${i}`}
            >
              <div className="text-3xl md:text-4xl font-bold tracking-tight mb-1 text-[#EAEAEA]">{s.value}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Interactive grid: thumbnails + featured */}
        <div
          className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="cases-interactive-grid"
        >
          {/* THUMBNAILS COLUMN */}
          <div className="lg:col-span-2 space-y-3">
            {cases.map((c, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={c.case_id}
                  onClick={() => setActiveIdx(i)}
                  onMouseEnter={() => setActiveIdx(i)}
                  data-testid={`case-thumb-${i}`}
                  className={`group w-full text-left p-5 rounded-xl border transition-all duration-400 accent-focus relative overflow-hidden ${
                    isActive
                      ? 'bg-accent-orange text-white border-accent-orange'
                      : 'bg-[#1A1A1A] text-[#EAEAEA] border-[#333333] hover:border-accent-orange'
                  }`}
                >
                  {/* Active progress bar */}
                  {isActive && !paused && (
                    <div
                      className="absolute top-0 left-0 h-0.5 bg-white"
                      style={{
                        width: '100%',
                        animation: 'progress-bar 5s linear forwards',
                      }}
                    ></div>
                  )}

                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-mono uppercase tracking-widest mb-1 ${
                        isActive ? 'text-white/80' : 'text-accent-orange'
                      }`}>
                        {String(i + 1).padStart(2, '0')} · {c.state || c.franchise_name}
                      </div>
                      <div className="font-semibold text-base truncate">{c.company}</div>
                      <div className={`text-xs mt-1 line-clamp-2 ${
                        isActive ? 'text-white/80' : 'text-neutral-500'
                      }`}>
                        {c.description}
                      </div>
                    </div>
                    <iconify-icon
                      icon="solar:arrow-right-up-linear"
                      className={`text-2xl flex-shrink-0 transition-transform duration-300 ${
                        isActive ? 'text-white translate-x-1' : 'text-neutral-600 group-hover:text-accent-orange group-hover:translate-x-1'
                      }`}
                    ></iconify-icon>
                  </div>
                </button>
              );
            })}
          </div>

          {/* FEATURED CARD */}
          <div className="lg:col-span-3">
            {active && (
              <div
                key={active.case_id}
                className="relative h-full bg-gradient-to-br from-neutral-900 to-neutral-700 rounded-2xl p-8 md:p-10 text-white overflow-hidden border border-neutral-800"
                data-testid="case-featured"
                style={{ animation: 'feature-fade 600ms ease-out' }}
              >
                {/* Background grain + orange glow accent */}
                <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none"></div>
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-accent-orange opacity-20 blur-3xl rounded-full pointer-events-none"></div>

                <div className="relative z-10 h-full flex flex-col">
                  {/* Logo / fallback monogram */}
                  <div className="mb-6">
                    {active.image ? (
                      <img src={active.image} alt={active.company} className="h-14 max-w-[180px] object-contain brightness-0 invert opacity-90" />
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-2xl font-bold">
                        {(active.company || '?').charAt(0)}
                      </div>
                    )}
                  </div>

                  {/* Tag */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-accent-orange/20 text-accent-orange border border-accent-orange/30 rounded-full text-xs font-mono uppercase tracking-wider">
                      {t('cases.tag')}
                    </span>
                    {active.franchise_name && (
                      <span className="px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-xs font-mono">
                        {active.franchise_name}
                      </span>
                    )}
                  </div>

                  <h3 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
                    {active.company}
                  </h3>
                  <p className="text-lg text-white/80 leading-relaxed mb-8 flex-1">
                    {active.description}
                  </p>

                  {/* Footer counters */}
                  <div className="flex items-center justify-between pt-6 border-t border-white/10">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50">
                      <span className="w-2 h-2 rounded-full bg-accent-orange accent-pulse"></span>
                      {String(activeIdx + 1).padStart(2, '0')} / {String(cases.length).padStart(2, '0')}
                    </div>
                    <a
                      href="/contato"
                      className="accent-icon group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white hover:text-accent-orange transition-colors"
                      data-testid="cases-cta-contact"
                    >
                      {t('cases.cta')}
                      <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* keyframes — scoped via global style tag because Tailwind doesn't ship custom keyframes here */}
      <style>{`
        @keyframes progress-bar {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes feature-fade {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default Cases;
